import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { ApiError } from "@/api/client";
import { Alert, TextField } from "@/components/ui";
import { AuthLayout } from "./AuthLayout";
import { useAuth } from "./AuthProvider";

const schema = z.object({
  workspace: z.string().min(3, "Enter your workspace name"),
  email: z.string().email("Enter a valid email"),
  password: z.string().min(1, "Enter your password"),
});
type Values = z.infer<typeof schema>;
interface SsoOption { sso: boolean; label?: string; enforced?: boolean }

/** Whether this workspace signs in through its identity provider (asked once typing pauses). */
function useSsoOption(workspace: string): SsoOption | null {
  const [option, setOption] = useState<SsoOption | null>(null);
  useEffect(() => {
    const slug = workspace.trim().toLowerCase();
    if (slug.length < 3) { setOption(null); return; }
    const timer = window.setTimeout(() => {
      fetch(`/api/v1/auth/sso/${encodeURIComponent(slug)}`)
        .then(async (r) => setOption(r.ok ? ((await r.json()) as SsoOption) : null))
        .catch(() => setOption(null));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [workspace]);
  return option;
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(params.get("sso_error"));
  const [usePassword, setUsePassword] = useState(false);
  const { register, handleSubmit, formState, watch } = useForm<Values>({
    resolver: zodResolver(schema), defaultValues: { workspace: params.get("workspace") ?? "" },
  });
  const workspace = watch("workspace") ?? "";
  const sso = useSsoOption(workspace);
  const passwordHidden = !!sso?.sso && !!sso.enforced && !usePassword;

  const onSubmit = handleSubmit(async ({ workspace, email, password }) => {
    setError(null);
    try {
      await login(workspace, email, password);
      const from = (location.state as { from?: string } | null)?.from ?? "/";
      navigate(from, { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not sign in. Try again.");
    }
  });

  return (
    <AuthLayout title="Sign in" subtitle="Welcome back. Sign in to your workspace.">
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {error && <Alert>{error}</Alert>}
        <TextField label="Workspace" autoComplete="organization" placeholder="acme" {...register("workspace")} error={formState.errors.workspace?.message} />
        {sso?.sso && (
          <div className="space-y-2">
            <a href={`/api/v1/auth/sso/${encodeURIComponent(workspace.trim().toLowerCase())}/start`} className="btn-primary w-full">
              Sign in with {sso.label}
            </a>
            {passwordHidden
              ? <p className="text-center text-xs text-muted">Your company signs in through {sso.label}.{" "}
                  <button type="button" className="font-medium text-brand hover:underline" onClick={() => setUsePassword(true)}>Workspace owner? Use your password</button></p>
              : <p className="text-center text-xs text-muted">or with your email and password</p>}
          </div>
        )}
        {!passwordHidden && <>
          <TextField label="Email" type="email" autoComplete="email" {...register("email")} error={formState.errors.email?.message} />
          <TextField label="Password" type="password" autoComplete="current-password" {...register("password")} error={formState.errors.password?.message} />
          <button type="submit" className={sso?.sso ? "btn-ghost w-full" : "btn-primary w-full"} disabled={formState.isSubmitting}>
            {formState.isSubmitting ? "Signing in…" : "Sign in"}
          </button>
        </>}
        <p className="text-center text-sm text-muted">
          New here? <Link to="/signup" className="font-medium text-brand hover:underline">Create a workspace</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
